import { useCallback, useEffect, useRef, useState } from "react";
import api, { API, readCsrfToken } from "../../lib/api";
import { consumeSSE } from "../../lib/sse";
import { useSentinelBusy } from "../../lib/sentinel-busy";

/**
 * useStreamingChat — fetches existing chat history and provides a `send`
 * function that streams tokens via SSE into the messages array. After each
 * completed turn it also asks the backend for 4 context-aware follow-up
 * prompt suggestions to surface in the UI.
 *
 * Resilience: if the first stream attempt fails before any token arrives
 * (typical cold-start / proxy timeout), we transparently retry once. The
 * user sees a brief "retrying" hint instead of an error.
 */

const HTML_ERR_REGEX = /<!DOCTYPE|<html|<body|Gateway time-out|504|502/i;

function friendlyError(raw) {
  const msg = (raw && raw.message) || raw || "unknown error";
  if (HTML_ERR_REGEX.test(msg)) {
    return "SENTINEL request timed out before the model responded. Please try again — Claude is occasionally slow on cold start.";
  }
  return msg;
}

export default function useStreamingChat() {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const { startBusy } = useSentinelBusy();
  const followupSeqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    api.get("/ai/history")
      .then((r) => {
        if (cancelled) return;
        // If a send() has already populated messages (e.g. the demo
        // auto-starter fired between StrictMode's double-mount passes),
        // never clobber it — only seed when still empty.
        setMessages((cur) => (cur.length === 0
          ? r.data.map((m, i) => ({ ...m, _k: m.id || `${m.ts}-${i}` }))
          : cur));
      })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const patchAssistant = useCallback((key, mutate) => {
    setMessages((m) => m.map((msg) => (msg._k === key ? mutate(msg) : msg)));
  }, []);

  const fetchFollowups = useCallback(async (lastUser, lastAssistant) => {
    if (!lastUser || !lastAssistant) return;
    const mySeq = ++followupSeqRef.current;
    setFollowupsLoading(true);
    try {
      const r = await api.post("/ai/followups", {
        last_user: lastUser.slice(0, 2000),
        last_assistant: lastAssistant.slice(0, 8000),
      });
      if (mySeq !== followupSeqRef.current) return;
      const arr = Array.isArray(r.data?.suggestions) ? r.data.suggestions : [];
      setFollowups(arr.slice(0, 4));
    } catch {
      if (mySeq === followupSeqRef.current) setFollowups([]);
    } finally {
      if (mySeq === followupSeqRef.current) setFollowupsLoading(false);
    }
  }, []);

  /**
   * Run a single stream attempt. Returns `{reply, ok, error, tokensReceived}`.
   * Always patches the assistant message in-place as tokens arrive.
   */
  const runStream = useCallback(async (text, assistantKey) => {
    let reply = "";
    let tokensReceived = false;
    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(readCsrfToken() ? { "X-CSRF-Token": readCsrfToken() } : {}),
        },
        body: JSON.stringify({ message: text }),
      });

      // Demo cost-cap responses arrive as 429 JSON before the SSE body.
      // Parse them out and surface a CTA instead of bleeding the raw payload
      // through the generic SSE error path.
      if (res.status === 429) {
        let detail = null;
        try { detail = (await res.json())?.detail; } catch { /* non-JSON */ }
        if (detail && typeof detail === "object") {
          return {
            reply: "",
            ok: false,
            error: null,
            tokensReceived: false,
            limit: detail,
          };
        }
        return { reply, ok: false, error: new Error("Rate limit reached"), tokensReceived };
      }

      let serverError = null;
      await consumeSSE(res, ({ event, data }) => {
        if (event === "delta") {
          tokensReceived = true;
          reply += data;
          patchAssistant(assistantKey, (msg) => ({ ...msg, content: msg.content + data }));
        } else if (event === "error") {
          serverError = data || "stream error";
          return false;
        } else if (event === "done") {
          return false;
        }
        return true;
      });
      if (serverError) return { reply, ok: false, error: serverError, tokensReceived };
      return { reply, ok: true, error: null, tokensReceived };
    } catch (e) {
      return { reply, ok: false, error: e, tokensReceived };
    }
  }, [patchAssistant]);

  const send = useCallback(async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    setBusy(true);
    setFollowups([]);
    const stopBusy = startBusy();
    const ts = new Date().toISOString();
    const userKey = `u-${Date.now()}`;
    const assistantKey = `a-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { role: "user", content: t, ts, _k: userKey },
      { role: "assistant", content: "", ts, _k: assistantKey, streaming: true },
    ]);

    // First attempt
    let result = await runStream(t, assistantKey);

    // Demo cost-cap hit — surface CTA, no retry.
    if (result.limit) {
      patchAssistant(assistantKey, (msg) => ({
        ...msg,
        content: `[DEMO LIMIT] ${result.limit.message}`,
        streaming: false,
        retrying: false,
        cta: result.limit.cta || null,
      }));
      stopBusy();
      setBusy(false);
      return;
    }

    // Transparent single retry — only if the first attempt errored AND
    // produced no tokens at all (so we know nothing was partially saved
    // and we won't double-up content in the chat window).
    if (!result.ok && !result.tokensReceived) {
      patchAssistant(assistantKey, (msg) => ({
        ...msg,
        content: "",
        streaming: true,
        retrying: true,
      }));
      // Tiny pause lets transient proxy/Claude issues clear.
      await new Promise((r) => setTimeout(r, 800));
      result = await runStream(t, assistantKey);
      patchAssistant(assistantKey, (msg) => ({ ...msg, retrying: false }));
      // Cap check after retry too — if the second attempt hits the limit,
      // surface CTA.
      if (result.limit) {
        patchAssistant(assistantKey, (msg) => ({
          ...msg,
          content: `[DEMO LIMIT] ${result.limit.message}`,
          streaming: false,
          retrying: false,
          cta: result.limit.cta || null,
        }));
        stopBusy();
        setBusy(false);
        return;
      }
    }

    if (result.ok) {
      patchAssistant(assistantKey, (msg) => ({ ...msg, streaming: false }));
    } else {
      const friendly = friendlyError(result.error);
      patchAssistant(assistantKey, (msg) => ({
        ...msg,
        content: `[SENTINEL OFFLINE] ${friendly}`,
        streaming: false,
        retrying: false,
      }));
    }

    stopBusy();
    setBusy(false);

    if (result.ok && result.reply.trim()) {
      fetchFollowups(t, result.reply);
    }
  }, [patchAssistant, startBusy, fetchFollowups, runStream]);

  return { messages, busy, send, loaded, followups, followupsLoading };
}
